import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { RestapiService } from '@app/services/restapi.service';
import { ComparisonMode, PeriodDateSearchFields, SortOrder, SummaryReportView } from '@app/services/summary.service';
import { PuSubscribable } from '@app/utils/pu-subscribe';
import { RestUserModel } from '@shared/interfaces/user.interface';

export interface SummaryReportSettingsModelBase {
  name: string;
  description?: string;
  dateUpdated?: Date | string;
  summaryType: SummaryType;
  dataSource: SummaryDataSource;
  reportView: SummaryReportView;
  sortBy: SortByInfoModel[];
  showBy?: ShowByFieldInfoModel[];
  filterBy?: FilterByFieldInfoModel[];
  dataSourceModifier?: string;
  periodType?: PeriodType;
  startDate?: string;
  endDate?: string;
  periodBType?: PeriodType | PeriodTypeExtended;
  periodBStartDate?: string;
  periodBEndDate?: string;
  rolesCanView?: number[];
  rolesCanEdit?: number[];
  defaultForRoles?: number[];
  rolesHaveOnDashboard?: number[];
  reportLayout?: number; // bitmask of SummaryLayoutType[]
  vsModeEnabled?: boolean;
  vsModeComparisonMode?: ComparisonMode;
  periodDateSearchFields?: PeriodDateSearchFields;
}

export interface SummaryReportSettingsModel extends SummaryReportSettingsModelBase {
  id?: number;
  dateCreated?: string;
  createdBy?: RestUserModel;
}

export interface ReportQueryParams {
  portfolioId?: number;
  propertyIds?: number[];
  collectionIds?: number[];
  summaryReportSettings: SummaryReportSettingsModel;
}

export interface BoardSummaryReportQueryParams extends ReportQueryParams {
  queryType: number;
  dateFieldsLookupMode: number;
  includeUnscheduled: boolean;
  reportView: number;
}

export interface TicketsSummaryReportQueryParams extends ReportQueryParams {
  excludeBoardTickets: boolean;
  periodDateSearchFields: number;
}

export interface InspectionSummaryReportQueryParams extends ReportQueryParams {
  isResident: boolean;
}

export interface TurnoverSummaryReportQueryParams extends ReportQueryParams {
  queryType: number;
  dateFieldsLookupMode: number;
  includeUnscheduled: boolean;
  reportView: number;
}

export enum SummaryReportStatus {
  NotStarted,
  InProgress,
  Completed,
  Error,
}

export interface SummaryReportOffline {
  id: number;
  reportType: number;
  payload: string;
  status: SummaryReportStatus;
  createdBy: number;
  createdAt: Date;
  lockedTill: Date;
  generatedOn?: Date;
  statusDetails?: string;
  fileUrl?: string;
}

export enum SummaryType {
  Turns,
  BB,
  Inspection,
  Service,
  TurnsList,
  BBList,
  InspectionList,
  ServiceList,
  ResidentInspectionsList,
  BoardsUsage,
  UserSummary,
}

export const DASHBOARD_SUMMARY_TYPES: SummaryType[] = [
  SummaryType.Turns,
  SummaryType.BB,
  SummaryType.Inspection,
  SummaryType.Service,
  SummaryType.BoardsUsage,
  SummaryType.UserSummary,
];

export enum SummaryDataSource {
  BB_All,
  BB_ById,
  BB_ByType,
  Inspection_Standard_All,
  Inspection_Recurring,
  Service_All,
  Service_OpenTickets,
  Service_CompletedByDate,
  Service_ServiceTickets,
  Turns_All,
  Turns_ById,
  Turns_ByType,
  Inspection_Resident_All,
  BoardsUsage_All,
  BoardsUsage_ByID,
  UserSummary_All,
  UserSummary_ByID,
}

export interface SortByInfoModel {
  columnName?: string;
  isDescending?: boolean;
}

export interface ShowByFieldInfoModel {
  label?: string;
  customFieldId?: number;
  fieldType?: number;
  isDescending?: boolean;
  sortOrder?: SortOrder;
}

export interface FilterByFieldInfoModel {
  customFieldId?: number;
  fieldType?: number;
  customFieldModifier?: number;
  values?: string;
  labels?: string;
}

export interface SummarySelectFilterConfig {
  id: number;
  label: string;
}

export enum PeriodType {
  Last7Days,
  Last30Days,
  Last60Days,
  Last3Months,
  Last6Month,
  LastYear,
  Custom,
  ThisWeek,
  Next30Days,
  Next60Days,
  Next6Month,
  ThisYear,
  Next2Weeks,
  ThisMonth,
  ThisQuarter,
  AllTime,
}

export enum PeriodTypeExtended {
  Last7Days,
  Last30Days,
  Last60Days,
  Last3Months,
  Last6Month,
  LastYear,
  Custom,
  ThisWeek,
  Next30Days,
  Next60Days,
  Next6Month,
  ThisYear,
  Next2Weeks,
  ThisMonth,
  ThisQuarter,
  AllTime,
  Prior7Days,
  Prior30Days,
  Prior60Days,
  Prior3Months,
  Prior6Months,
  PriorQuarter,
  PriorYear,
}

export const FilterPeriodConfig: SummarySelectFilterConfig[] = [
  {
    id: PeriodType.Last7Days,
    label: 'Last 7 days',
  },
  {
    id: PeriodType.Last30Days,
    label: 'Last 30 days',
  },
  {
    id: PeriodType.Last60Days,
    label: 'Last 60 days',
  },
  {
    id: PeriodType.Last3Months,
    label: 'Last 3 months',
  },
  {
    id: PeriodType.Last6Month,
    label: 'Last 6 months',
  },
  {
    id: PeriodType.LastYear,
    label: 'Last year',
  },
  {
    id: PeriodType.Custom,
    label: 'Custom',
  },
  {
    id: PeriodType.ThisWeek,
    label: 'This week',
  },
  {
    id: PeriodType.ThisMonth,
    label: 'This month',
  },
  {
    id: PeriodType.ThisQuarter,
    label: 'This quarter',
  },
  {
    id: PeriodType.ThisYear,
    label: 'This year',
  },
  {
    id: PeriodType.Next30Days,
    label: 'Next 30 days',
  },
  {
    id: PeriodType.Next60Days,
    label: 'Next 60 days',
  },
  {
    id: PeriodType.Next2Weeks,
    label: 'Next 2 weeks',
  },
  {
    id: PeriodType.Next6Month,
    label: 'Next 6 months',
  },
  {
    id: PeriodType.AllTime,
    label: 'All time',
  },
];

export const FilterPeriodConfigExtended: SummarySelectFilterConfig[] = [
  {
    id: PeriodTypeExtended.Prior7Days,
    label: 'Prior 7 days',
  },
  {
    id: PeriodTypeExtended.Prior30Days,
    label: 'Prior 30 days',
  },
  {
    id: PeriodTypeExtended.Prior60Days,
    label: 'Prior 60 days',
  },
  {
    id: PeriodTypeExtended.Prior3Months,
    label: 'Prior 90 days',
  },
  {
    id: PeriodTypeExtended.PriorQuarter,
    label: 'Prior quarter',
  },
  {
    id: PeriodTypeExtended.Prior6Months,
    label: 'Prior 6 months',
  },
  {
    id: PeriodTypeExtended.PriorYear,
    label: 'Prior year',
  },
  {
    id: PeriodTypeExtended.Last7Days,
    label: 'Last 7 days',
  },
  {
    id: PeriodTypeExtended.Last30Days,
    label: 'Last 30 days',
  },
  {
    id: PeriodTypeExtended.Last60Days,
    label: 'Last 60 days',
  },
  {
    id: PeriodTypeExtended.Last3Months,
    label: 'Last 90 days',
  },
  {
    id: PeriodTypeExtended.Last6Month,
    label: 'Last 6 months',
  },
  {
    id: PeriodTypeExtended.LastYear,
    label: 'Last year',
  },
  {
    id: PeriodTypeExtended.Custom,
    label: 'Custom',
  },
  {
    id: PeriodTypeExtended.ThisWeek,
    label: 'This week',
  },
  {
    id: PeriodTypeExtended.Next30Days,
    label: 'Next 30 days',
  },
  {
    id: PeriodTypeExtended.Next60Days,
    label: 'Next 60 days',
  },
  {
    id: PeriodTypeExtended.Next6Month,
    label: 'Next 6 months',
  },
  {
    id: PeriodTypeExtended.ThisYear,
    label: 'This year',
  },
  {
    id: PeriodTypeExtended.Next2Weeks,
    label: 'Next 2 weeks',
  },
  {
    id: PeriodTypeExtended.ThisMonth,
    label: 'This month',
  },
  {
    id: PeriodTypeExtended.ThisQuarter,
    label: 'This quarter',
  },
  {
    id: PeriodTypeExtended.AllTime,
    label: 'All time',
  },
];

export interface SummaryReportSettingsAddToDashboardModel {
  roleId: number;
  summarySettingsReportIds: number[];
}

@Injectable({
  providedIn: 'root',
})
export class SummaryReportSettingsService extends PuSubscribable {
  private readonly route = 'SummaryReportSettings';

  constructor(private restapiService: RestapiService) {
    super();
  }

  getAll(): Observable<SummaryReportSettingsModel[]> {
    return this.restapiService.getData<SummaryReportSettingsModel[]>(this.route);
  }

  add(setting: SummaryReportSettingsModelBase): Observable<SummaryReportSettingsModel> {
    return this.restapiService.post<SummaryReportSettingsModel>(this.route, setting);
  }

  update(id: number, setting: SummaryReportSettingsModel): Observable<SummaryReportSettingsModel> {
    return this.restapiService.update<SummaryReportSettingsModel>(`${this.route}/${id}`, setting);
  }

  delete(id: number): Observable<void> {
    return this.restapiService.delete<void>(`${this.route}/${id}`);
  }

  getReportStatus(id: number): Observable<SummaryReportOffline> {
    return this.restapiService.getData<SummaryReportOffline>(`exportReportRequests/${id}`);
  }

  getDashboardByRole(roleId: number): Observable<SummaryReportSettingsModel[]> {
    return this.restapiService.getData<SummaryReportSettingsModel[]>(`${this.route}/dashboardByRole/${roleId}`);
  }

  addToDashboard(model: SummaryReportSettingsAddToDashboardModel): Observable<void> {
    return this.restapiService.post<void>(`${this.route}/addToDashboard`, model);
  }

  removeFromDashboard(model: SummaryReportSettingsAddToDashboardModel): Observable<void> {
    return this.restapiService.delete<void>(`${this.route}/addToDashboard`, model);
  }
}
